import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/LanguageContext";
import { Eyebrow, Reveal } from "@/components/Motion";

export default function PackagingShowcase() {
  const { c } = useLang();
  const p = c.packaging;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const boxY = useTransform(scrollYProgress, [0, 1], ["12%", "-12%"]);

  return (
    <section id="packaging" ref={ref} data-testid="packaging-section" className="relative py-28 sm:py-40 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#12501f] via-[#2f8a43] to-[#1a5e2b]" />
        <img src="https://images.unsplash.com/photo-1698764700248-c3798485f6f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c3317] via-[#0c3317]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c3317]/40 via-transparent to-[#0c3317]/40" />
        <img src="/leaves-corner.png" alt="" aria-hidden="true" style={{ mixBlendMode: "screen" }} className="pointer-events-none absolute top-0 right-0 w-[440px] -mt-16 -mr-20 rotate-6 opacity-55" />
        <img src="/leaves-side.png" alt="" aria-hidden="true" style={{ mixBlendMode: "screen" }} className="pointer-events-none absolute bottom-0 left-0 h-[420px] -ml-16 -rotate-12 opacity-45 hidden md:block" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        {/* copy */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Eyebrow testid="packaging-eyebrow">{p.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-serif font-light leading-[1.0] tracking-[-0.02em] text-cream text-5xl sm:text-6xl lg:text-7xl">
            {p.heading.pre}<span className="text-gold italic">{p.heading.em}</span>
          </h2>
          <p className="mt-6 font-sans text-sm sm:text-base leading-relaxed text-cream/65 max-w-md">{p.subcopy}</p>

          <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-7 border-t border-gold/20 pt-8">
            {p.specs.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`packaging-spec-${i}`}>
                  <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold mb-2">{s.label}</div>
                  <div className="font-serif text-xl sm:text-2xl text-cream leading-tight">{s.value}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* box */}
        <div className="lg:col-span-7 order-1 lg:order-2 relative">
          {/* spotlight */}
          <div aria-hidden className="absolute inset-0 -z-10 flex items-center justify-center">
            <div className="h-[75%] w-[75%] rounded-full bg-[radial-gradient(circle,rgba(212,163,59,0.22),transparent_62%)] blur-xl" />
          </div>
          <motion.div
            style={{ y: boxY }}
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <img
              src="/greengold-box.png"
              alt="Green Gold premium Venezuelan plantains export box — 50 lbs, Sur del Lago"
              data-testid="packaging-box-image"
              className="w-full h-auto drop-shadow-[0_40px_60px_rgba(0,0,0,0.55)]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
